import { useEffect, useRef, useState } from 'react'

import { sfx } from '../../../lib/sound'
import type { GameProps } from '../GameShell'

const W = 480
const H = 200
const GROUND = H - 30
const RUNNER_X = 60
const RUNNER_SIZE = 22
const GRAVITY = 0.75
const JUMP = -12.5

interface Obstacle {
  x: number
  w: number
  h: number
}

interface RunnerState {
  y: number
  vy: number
  grounded: boolean
  obstacles: Obstacle[]
  speed: number
  distance: number
  spawnIn: number
  running: boolean
}

export default function Runner({ onGameOver, onScore }: GameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)

  const state = useRef<RunnerState>({
    y: GROUND - RUNNER_SIZE,
    vy: 0,
    grounded: true,
    obstacles: [],
    speed: 4,
    distance: 0,
    spawnIn: 60,
    running: true,
  })

  // One button, any button.
  useEffect(() => {
    function jump() {
      const s = state.current
      if (s.grounded && s.running) {
        s.vy = JUMP
        s.grounded = false
        sfx.pop()
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === ' ' || event.key === 'ArrowUp' || event.key === 'w') {
        event.preventDefault()
        jump()
      }
    }
    const canvas = canvasRef.current
    window.addEventListener('keydown', onKey)
    canvas?.addEventListener('pointerdown', jump)
    return () => {
      window.removeEventListener('keydown', onKey)
      canvas?.removeEventListener('pointerdown', jump)
    }
  }, [])

  useEffect(() => {
    const maybeCtx = canvasRef.current?.getContext('2d')
    if (!maybeCtx) return
    // Rebound with an explicit type: the narrowing from the guard above does
    // not reach inside the hoisted `step` function below.
    const ctx: CanvasRenderingContext2D = maybeCtx
    let frame = 0

    function step() {
      const s = state.current
      if (!s.running) return

      // Physics.
      s.vy += GRAVITY
      s.y += s.vy
      if (s.y >= GROUND - RUNNER_SIZE) {
        s.y = GROUND - RUNNER_SIZE
        s.vy = 0
        s.grounded = true
      }

      // The world speeds up with distance, which is the only difficulty curve
      // a one-button game needs.
      s.distance += s.speed
      s.speed = Math.min(11, 4 + s.distance / 2200)

      s.obstacles = s.obstacles
        .map((o) => ({ ...o, x: o.x - s.speed }))
        .filter((o) => o.x + o.w > -10)

      s.spawnIn -= 1
      if (s.spawnIn <= 0) {
        const height = 20 + Math.random() * 26
        s.obstacles.push({ x: W + 20, w: 14 + Math.random() * 14, h: height })
        // Gap scales with speed so the game stays possible as it accelerates.
        const minGap = 55 + s.speed * 4
        s.spawnIn = minGap + Math.random() * 50
      }

      // Collision, with a slightly forgiving box -- pixel-exact hit detection
      // on a fast runner reads as unfair.
      const rx = RUNNER_X + 3
      const rw = RUNNER_SIZE - 6
      for (const o of s.obstacles) {
        const hit =
          rx < o.x + o.w &&
          rx + rw > o.x &&
          s.y + RUNNER_SIZE > GROUND - o.h + 2
        if (hit) {
          s.running = false
          sfx.thud()
          onGameOver(Math.floor(s.distance / 10))
          return
        }
      }

      const points = Math.floor(s.distance / 10)
      setScore(points)
      onScore?.(points)

      draw(ctx, s)
      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    // Captured rather than read in cleanup: the ref object never changes, but
    // reading `.current` at teardown is the pattern that bites when it does.
    const game = state.current
    return () => {
      cancelAnimationFrame(frame)
      game.running = false
    }
  }, [onGameOver, onScore])

  return (
    <div className="space-y-3">
      <p className="text-center text-sm text-muted">
        {score} · space, tap or ↑ to jump
      </p>
      <canvas
        ref={canvasRef}
        width={W}
        height={H}
        className="mx-auto w-full max-w-lg cursor-pointer touch-none rounded-xl border border-border-subtle bg-canvas"
      />
    </div>
  )
}

function draw(ctx: CanvasRenderingContext2D, s: RunnerState) {
  ctx.clearRect(0, 0, W, H)

  // Ground line.
  ctx.strokeStyle = '#6b7280'
  ctx.beginPath()
  ctx.moveTo(0, GROUND)
  ctx.lineTo(W, GROUND)
  ctx.stroke()

  // Obstacles.
  ctx.fillStyle = '#ef4444'
  for (const o of s.obstacles) {
    ctx.fillRect(o.x, GROUND - o.h, o.w, o.h)
  }

  // The runner. A square that squashes slightly while airborne, which is
  // enough to make the jump feel like it has weight.
  const airborne = !s.grounded
  ctx.fillStyle = '#6366f1'
  ctx.fillRect(
    RUNNER_X,
    s.y + (airborne ? 2 : 0),
    RUNNER_SIZE,
    RUNNER_SIZE - (airborne ? 4 : 0),
  )
}
